import { useEffect, useState, useRef } from 'react';
import { Calendar, Clock, AlertCircle, Grid3x3, List, LayoutGrid, Filter, X, Search, ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { useCalendarStore } from '@/store/calendarStore';
import { useAuthStore } from '@/store/authStore';
import { useClientStore } from '@/store/clientStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import CalendarMonthView from '@/components/calendar/CalendarMonthView';
import CalendarWeekView from '@/components/calendar/CalendarWeekView';
import CalendarDayView from '@/components/calendar/CalendarDayView';
import EventCard from '@/components/calendar/EventCard';
import DeadlineBadge from '@/components/calendar/DeadlineBadge';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addMonths, subMonths, addWeeks, subWeeks, isToday, isSameDay } from 'date-fns';
import { it } from 'date-fns/locale';
import { Link, useNavigate } from 'react-router-dom';

export default function CalendarPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const {
    events,
    loading,
    error,
    currentDate,
    selectedDate,
    view,
    fetchEvents,
    setCurrentDate,
    setSelectedDate,
    setView,
  } = useCalendarStore();

  const { clients, fetchClients } = useClientStore();

  const [showFilters, setShowFilters] = useState(false);
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('all');
  const [clientFilter, setClientFilter] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const eventsListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const today = new Date();
    const start = format(startOfMonth(today), 'yyyy-MM-dd');
    const end = format(endOfMonth(today), 'yyyy-MM-dd');
    // Non passare userId per mostrare tutti gli eventi
    fetchEvents(start, end, clientFilter || undefined, undefined, undefined);
    setCurrentDate(today);
    fetchClients({ page: 1, limit: 1000 });
  }, [fetchClients, fetchEvents]);

  useEffect(() => {
      // Ricarica eventi quando cambiano i filtri
      if (currentDate) {
        const start = view === 'month' 
          ? format(startOfMonth(currentDate), 'yyyy-MM-dd')
          : view === 'week'
          ? format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'yyyy-MM-dd')
          : view === 'day'
          ? format(currentDate, 'yyyy-MM-dd')
          : format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        
        const end = view === 'month'
          ? format(endOfMonth(currentDate), 'yyyy-MM-dd')
          : view === 'week'
          ? format(endOfWeek(currentDate, { weekStartsOn: 1 }), 'yyyy-MM-dd')
          : view === 'day'
          ? format(currentDate, 'yyyy-MM-dd')
          : format(addWeeks(startOfWeek(currentDate, { weekStartsOn: 1 }), 4), 'yyyy-MM-dd');
        
        // Non passare userId per mostrare tutti gli eventi
        fetchEvents(start, end, clientFilter || undefined, undefined, undefined);
      }
  }, [view, currentDate, clientFilter]);

  const handleViewChange = (newView: 'month' | 'week' | 'day' | 'list') => {
    setView(newView);
  };

  const handleDateChange = (direction: 'prev' | 'next') => {
    let newDate: Date;
    if (view === 'month') {
      newDate = direction === 'next' ? addMonths(currentDate, 1) : subMonths(currentDate, 1);
    } else if (view === 'week') {
      newDate = direction === 'next' ? addWeeks(currentDate, 1) : subWeeks(currentDate, 1);
    } else if (view === 'day') {
      newDate = direction === 'next' ? new Date(currentDate.getTime() + 24 * 60 * 60 * 1000) : new Date(currentDate.getTime() - 24 * 60 * 60 * 1000);
    } else {
      newDate = direction === 'next' ? addWeeks(currentDate, 1) : subWeeks(currentDate, 1);
    }
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
    // Scroll alla sezione eventi dopo un breve delay per permettere il rendering
    setTimeout(() => {
      if (eventsListRef.current) {
        eventsListRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleEventClick = (event: any) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  // Filtri eventi
  const filteredEvents = events.filter(event => {
    if (eventTypeFilter !== 'all' && event.type !== eventTypeFilter) return false;
    if (clientFilter && event.client !== clientFilter) return false;
    if (searchFilter) {
      const searchLower = searchFilter.toLowerCase();
      return (
        event.title.toLowerCase().includes(searchLower) ||
        event.description?.toLowerCase().includes(searchLower) ||
        event.client?.toLowerCase().includes(searchLower) ||
        event.practice?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  // Eventi per una data specifica
  const getEventsForDate = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return filteredEvents.filter(event => {
      const eventDate = format(new Date(event.start), 'yyyy-MM-dd');
      return eventDate === dateStr;
    });
  };

  const selectedEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  // Per vista lista: tutti gli eventi ordinati per data
  const sortedEvents = [...filteredEvents].sort((a, b) => 
    new Date(a.start).getTime() - new Date(b.start).getTime()
  );

  const clearFilters = () => {
    setEventTypeFilter('all');
    setClientFilter('');
    setSearchFilter('');
    setShowFilters(false);
  };

  if (loading && !events.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento calendario...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Calendario</h1>
          <p className="mt-1 text-sm text-gray-500">
            Visualizza appuntamenti e scadenze pratiche
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filtri
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleToday}
          >
            Oggi
          </Button>
        </div>
      </div>

      {/* Filtri */}
      {showFilters && (
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Cerca</label>
                  <Input
                    placeholder="Cerca eventi..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Tipo Evento</label>
                  <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti</SelectItem>
                      <SelectItem value="appointment">Appuntamenti</SelectItem>
                      <SelectItem value="practice_deadline">Scadenze</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Cliente</label>
                  <Select value={clientFilter || 'all'} onValueChange={(value) => setClientFilter(value === 'all' ? '' : value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Tutti i clienti" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti i clienti</SelectItem>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={`${client.firstName} ${client.lastName}`}>
                          {client.firstName} {client.lastName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button variant="outline" onClick={clearFilters} className="w-full">
                    <X className="h-4 w-4 mr-2" />
                    Rimuovi Filtri
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Switch Viste e Navigazione */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl">
              {view === 'month' && format(currentDate, 'MMMM yyyy', { locale: it })}
              {view === 'week' && `Settimana ${format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'dd MMM', { locale: it })} - ${format(endOfWeek(currentDate, { weekStartsOn: 1 }), 'dd MMM yyyy', { locale: it })}`}
              {view === 'day' && format(currentDate, 'EEEE d MMMM yyyy', { locale: it })}
              {view === 'list' && 'Lista Eventi'}
            </CardTitle>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 border rounded-lg">
                <Button
                  variant={view === 'month' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => handleViewChange('month')}
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button
                  variant={view === 'week' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => handleViewChange('week')}
                >
                  <Grid3x3 className="h-4 w-4" />
                </Button>
                <Button
                  variant={view === 'day' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => handleViewChange('day')}
                >
                  <CalendarDays className="h-4 w-4" />
                </Button>
                <Button
                  variant={view === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => handleViewChange('list')}
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDateChange('prev')}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDateChange('next')}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="min-h-[600px]">
          {/* Vista Mese */}
          {view === 'month' && (
            <CalendarMonthView
              currentDate={currentDate}
              events={filteredEvents}
              selectedDate={selectedDate}
              onDateClick={handleDateClick}
            />
          )}

          {/* Vista Settimana */}
          {view === 'week' && (
            <CalendarWeekView
              currentDate={currentDate}
              events={filteredEvents}
              selectedDate={selectedDate}
              onDateClick={handleDateClick}
            />
          )}

          {/* Vista Giornaliera */}
          {view === 'day' && (
            <CalendarDayView
              currentDate={currentDate}
              events={filteredEvents}
              selectedDate={selectedDate}
              onDateClick={handleDateClick}
            />
          )}

          {/* Vista Lista */}
          {view === 'list' && (
            <div className="space-y-3">
              {sortedEvents.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>Nessun evento trovato</p>
                </div>
              ) : (
                sortedEvents.map((event) => (
                  <div key={event.id} onClick={() => handleEventClick(event)}>
                    <EventCard event={event} />
                  </div>
                ))
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Eventi del giorno selezionato (solo per vista mese) */}
      {view === 'month' && selectedDate && selectedEvents.length > 0 && (
        <Card ref={eventsListRef}>
          <CardHeader>
            <CardTitle>
              Eventi per {format(selectedDate, 'dd MMMM yyyy', { locale: it })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="all">
              <TabsList>
                <TabsTrigger value="all">
                  Tutti ({selectedEvents.length})
                </TabsTrigger>
                <TabsTrigger value="appointments">
                  Appuntamenti ({selectedEvents.filter(e => e.type === 'appointment').length})
                </TabsTrigger>
                <TabsTrigger value="deadlines">
                  Scadenze ({selectedEvents.filter(e => e.type === 'practice_deadline').length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="all" className="space-y-3 mt-4">
                {selectedEvents.map((event) => (
                  <div key={event.id} onClick={() => handleEventClick(event)}>
                    <EventCard event={event} />
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="appointments" className="space-y-3 mt-4">
                {selectedEvents.filter(e => e.type === 'appointment').length > 0 ? (
                  selectedEvents
                    .filter(e => e.type === 'appointment')
                    .map((event) => (
                      <div key={event.id} onClick={() => handleEventClick(event)}>
                        <EventCard event={event} />
                      </div>
                    ))
                ) : (
                  <div className="text-center py-8 text-gray-500">Nessun appuntamento</div>
                )}
              </TabsContent>

              <TabsContent value="deadlines" className="space-y-3 mt-4">
                {selectedEvents.filter(e => e.type === 'practice_deadline').length > 0 ? (
                  selectedEvents
                    .filter(e => e.type === 'practice_deadline')
                    .map((event) => (
                      <div key={event.id} onClick={() => handleEventClick(event)}>
                        <EventCard event={event} />
                      </div>
                    ))
                ) : (
                  <div className="text-center py-8 text-gray-500">Nessuna scadenza</div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {view === 'month' && selectedDate && selectedEvents.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">Nessun evento per questa data</p>
          </CardContent>
        </Card>
      )}

      {/* Modal Dettagli Evento */}
      <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedEvent?.type === 'appointment' ? (
                <Clock className="h-5 w-5 text-blue-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600" />
              )}
              {selectedEvent?.title}
            </DialogTitle>
          </DialogHeader>
          {selectedEvent && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Data</label>
                  <p className="text-sm text-gray-900">
                    {format(new Date(selectedEvent.start), 'dd MM yyyy', { locale: it })}
                  </p>
                </div>
                {(selectedEvent.type === 'appointment' || selectedEvent.type === 'activity') && !selectedEvent.allDay && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Orario</label>
                    <p className="text-sm text-gray-900">
                      {format(new Date(selectedEvent.start), 'HH:mm', { locale: it })}
                      {selectedEvent.end && ` - ${format(new Date(selectedEvent.end), 'HH:mm', { locale: it })}`}
                    </p>
                  </div>
                )}
                {selectedEvent.client && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Cliente</label>
                    <p className="text-sm text-gray-900">{selectedEvent.client}</p>
                  </div>
                )}
                {selectedEvent.practice && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Pratica</label>
                    <p className="text-sm text-gray-900">{selectedEvent.practice}</p>
                  </div>
                )}
                {selectedEvent.location && (
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-gray-700">Luogo</label>
                    <p className="text-sm text-gray-900">{selectedEvent.location}</p>
                  </div>
                )}
                {selectedEvent.practiceType && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Tipo Pratica</label>
                    <p className="text-sm text-gray-900">{selectedEvent.practiceType}</p>
                  </div>
                )}
                {selectedEvent.status && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Stato</label>
                    <Badge variant={selectedEvent.status === 'COMPLETED' ? 'default' : 'secondary'}>
                      {selectedEvent.status}
                    </Badge>
                  </div>
                )}
                {selectedEvent.type === 'practice_deadline' && (
                  <div className="col-span-2">
                    <DeadlineBadge event={selectedEvent} />
                  </div>
                )}
              </div>
              {selectedEvent.description && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Descrizione</label>
                  <p className="text-sm text-gray-900 mt-1">{selectedEvent.description}</p>
                </div>
              )}
              <div className="pt-4 border-t flex items-center justify-between gap-2">
                {selectedEvent.type === 'appointment' && selectedEvent.id && (
                  <>
                    <Link to={`/practices/${selectedEvent.practiceId || ''}`} className="flex-1">
                      <Button variant="outline" size="sm" className="w-full">
                        Vai all'Appuntamento
                      </Button>
                    </Link>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        if (selectedEvent.practiceId) {
                          // Naviga alla pratica e apri il dialog di modifica appuntamento
                          navigate(`/practices/${selectedEvent.practiceId}`, { 
                            state: { editAppointmentId: selectedEvent.id } 
                          });
                        }
                        setShowEventModal(false);
                      }}
                    >
                      Modifica
                    </Button>
                  </>
                )}
                {selectedEvent.type === 'activity' && (selectedEvent.activityId || selectedEvent.id) && (
                  <>
                    <Link to={`/activities/${selectedEvent.activityId || selectedEvent.id.replace('activity-', '')}`} className="flex-1">
                      <Button variant="outline" size="sm" className="w-full">
                        Vai all'Attività
                      </Button>
                    </Link>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        const activityId = selectedEvent.activityId || selectedEvent.id.replace('activity-', '');
                        navigate(`/activities/${activityId}`);
                        setShowEventModal(false);
                      }}
                    >
                      Modifica
                    </Button>
                  </>
                )}
                {selectedEvent.type === 'practice_deadline' && selectedEvent.id && (
                  <>
                    <Link to={`/practices/${selectedEvent.id.replace('deadline-', '')}`} className="flex-1">
                      <Button variant="outline" size="sm" className="w-full">
                        Vai alla Pratica
                      </Button>
                    </Link>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setShowEventModal(false);
                        navigate(`/practices/${selectedEvent.id.replace('deadline-', '')}/edit`);
                      }}
                    >
                      Modifica
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
